import React, { useState, useEffect } from 'react';
import { profileService, UserGroup } from '../../services/profile.service';

interface GroupsTabProps {
  userId: string;
  isOwnProfile: boolean;
}

export function GroupsTab({ userId, isOwnProfile }: GroupsTabProps) {
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadGroups();
  }, [userId]);

  const loadGroups = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await profileService.getUserGroups(userId);
      setGroups(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar los grupos');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8 text-center">
        <svg className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
        <p className="text-gray-600 dark:text-gray-400">
          {isOwnProfile ? 'Aún no perteneces a ningún grupo' : 'Este usuario no pertenece a grupos públicos'}
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {groups.map((group) => (
        <div
          key={group.id}
          className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
        >
          {/* Cover/Banner */}
          {group.cover_url ? (
            <div
              className="h-24 bg-gradient-to-r from-violet-500 to-fuchsia-600"
              style={{
                backgroundImage: `url(${group.cover_url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          ) : (
            <div className="h-24 bg-gradient-to-r from-violet-500 to-fuchsia-600" />
          )}

          <div className="p-4">
            {/* Avatar */}
            <div className="-mt-12 mb-3">
              {group.avatar_url ? (
                <img
                  src={group.avatar_url}
                  alt={group.name}
                  className="w-16 h-16 rounded-full border-4 border-white dark:border-gray-800 object-cover bg-white"
                />
              ) : (
                <div className="w-16 h-16 rounded-full border-4 border-white dark:border-gray-800 bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center">
                  <span className="text-xl font-bold text-white">
                    {group.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </div>

            {/* Group Info */}
            <h3 className="font-bold text-gray-900 dark:text-white mb-1 truncate">
              {group.name}
            </h3>
            {group.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                {group.description}
              </p>
            )}

            {/* Badges */}
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  group.role === 'admin'
                    ? 'bg-fuchsia-100 dark:bg-fuchsia-900/30 text-fuchsia-800 dark:text-fuchsia-200'
                    : group.role === 'moderator'
                    ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-800 dark:text-violet-200'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                }`}
              >
                {group.role === 'admin' ? '👑 Admin' : group.role === 'moderator' ? '🛡️ Moderador' : '👤 Miembro'}
              </span>
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  group.privacy === 'public'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                    : 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200'
                }`}
              >
                {group.privacy === 'public' ? '🌍 Público' : '🔒 Privado'}
              </span>
            </div>

            {/* Join Date */}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
              Miembro desde{' '}
              {new Date(group.joined_at).toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
              })}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
